import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';

interface TiptapEditorProps {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  editable?: boolean;
}

/**
 * YAML frontmatter regex — leading `---\n…---\n` block.
 *
 * BlockNote's markdown parser doesn't recognise YAML frontmatter and
 * renders the literal `---` lines + key:value pairs as a heading + body
 * mess. Strip the block before parsing, re-prepend it on save so the
 * saved file still carries the agent's metadata header.
 * (aligns PP main #119)
 */
const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
function splitFrontmatter(md: string): { frontmatter: string; body: string } {
  if (!md || !md.startsWith('---')) return { frontmatter: '', body: md };
  const m = md.match(FRONTMATTER_RE);
  if (!m) return { frontmatter: '', body: md };
  return { frontmatter: m[0], body: md.slice(m[0].length) };
}

export default function TiptapEditor({ value, onChange, placeholder, editable = true }: TiptapEditorProps) {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  const editor = useCreateBlockNote({
    initialContent: undefined,
  });

  // Preserve frontmatter across strip-on-load / prepend-on-save so user
  // edits don't drop the agent's metadata header.
  const frontmatterRef = useRef<string>('');

  // Load initial markdown content
  useEffect(() => {
    if (!editor || !value) return;
    (async () => {
      try {
        const { frontmatter, body } = splitFrontmatter(value);
        frontmatterRef.current = frontmatter;
        const blocks = await editor.tryParseMarkdownToBlocks(body);
        editor.replaceBlocks(editor.document, blocks);
      } catch {}
    })();
  // Only on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. agent updates)
  useEffect(() => {
    if (!editor) return;
    (async () => {
      try {
        const current = await editor.blocksToMarkdownLossy(editor.document);
        const { frontmatter, body } = splitFrontmatter(value);
        if (current.trim() !== body.trim()) {
          frontmatterRef.current = frontmatter;
          const blocks = await editor.tryParseMarkdownToBlocks(body);
          editor.replaceBlocks(editor.document, blocks);
        } else {
          // Body unchanged — refresh cached frontmatter in case agent
          // updated only the metadata header.
          frontmatterRef.current = frontmatter;
        }
      } catch {}
    })();
  }, [value, editor]);

  const handleChange = useCallback(async () => {
    if (!editor || !onChange) return;
    try {
      const body = await editor.blocksToMarkdownLossy(editor.document);
      // Prepend stripped frontmatter so saved file keeps the metadata.
      onChange(frontmatterRef.current + body);
    } catch {}
  }, [editor, onChange]);

  // Observe theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      // BlockNote picks up theme via the `data-color-scheme` prop, handled by re-render
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const currentTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';

  return (
    <div className="bn-wrap" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <BlockNoteView
          editor={editor}
          theme={currentTheme}
          onChange={editable ? handleChange : undefined}
          editable={editable}
          data-placeholder={placeholder}
        />
      </div>
      <style>{`
        .bn-wrap .bn-editor {
          font-size: var(--fs-lg);
          line-height: 1.7;
          color: var(--text-primary);
        }
        .bn-wrap .bn-container {
          font-family: inherit;
        }
        .bn-wrap [class*="blockquote"] {
          border-left-color: var(--border);
        }
      `}</style>
    </div>
  );
}
